import { useEffect, useRef, useState } from "react";
import { CardBody, CardHeader, Form as RSForm } from "reactstrap";
import { Formik } from "formik";
import { User, FileSpreadsheet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { showSuccess } from "../components/AppToast";
import Sidebar from "../components/Sidebar";
import ProgressStepper from "../components/ProgressStepper";
import ImageUploadStep from "../components/ImageUploadStep";
import ImageCropModal from "../components/ImageCropModal";
import FormActions from "../components/FormActions";
import MerchantDetails from "../components/MerchantDetails";
import BasicDetails from "../components/FormDetails";
import ExcelUploadStep from "../components/ExcelUploadStep";
import FormSetup from "./FormSetup";

import {
  schemas,
  excelSchemas,
  defaultValues,
  saveDraft,
  STEP_LABELS,
  EXCEL_STEP_LABELS,
} from "../utils/formConfig";
import { getCroppedImage } from "../utils/imageUtils";
import UdfMapping from "./UdfMapping";

export default function CreateForm() {
  const formikRef = useRef(null);

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [formSaved, setFormSaved] = useState(false);
    const navigate = useNavigate();

  const currentSchemas = mode === "excel" ? excelSchemas : schemas;
  const currentStepLabels = mode === "excel" ? EXCEL_STEP_LABELS : STEP_LABELS;
  const totalSteps = currentSchemas.length + 1;

  // ── Draft autosave ──
  const formikValues = formikRef.current?.values;
  useEffect(() => {
    if (formikValues) saveDraft(formikValues);
  }, [formikValues]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const nextStep = async (formik) => {
    if (step === 0) {
      if (!mode) return;
      setStep(1);
      return;
    }

    const errors = await formik.validateForm();
    const schema = currentSchemas[step - 1];
    const stepFields = schema?.fields ? Object.keys(schema.fields) : [];
    const hasErrors = Object.keys(errors).some((k) => stepFields.includes(k));

    if (hasErrors) {
      stepFields.forEach((f) => formik.setFieldTouched(f, true, false));
      return;
    }

    setStep((s) => s + 1);
  };

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    formikRef.current?.setFieldValue("mode", selectedMode);
    setStep(1);
  };

  const prevStep = () => {
    if (step <= 1) {
      setStep(0);
      setMode(null);
      formikRef.current?.setFieldValue("mode", "");
    } else {
      setStep((s) => s - 1);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const submitFinal = async (values, actions) => {
    actions.setSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));

    actions.resetForm();
    setStep(0);
    setMode(null);
    localStorage.removeItem("px_multistep_draft");
    actions.setSubmitting(false);
    navigate("/my-forms");
    };

  // ── Image crop ────────────────────────────────────────────────────────────
  const handleCropSave = async (croppedPixels, enableCrop) => {
    if (enableCrop && croppedPixels && cropSrc) {
      try {
        const blob = await getCroppedImage(cropSrc, croppedPixels);
        const file = new File([blob], "avatar.png", { type: "image/png" });
        formikRef.current?.setFieldValue("avatar", file);
      } catch (err) {
        console.error("Crop failed:", err);
      }
    }
    setCropSrc(null);
  };

  const renderStep = (formik) => {
    if (mode === "manual") {
      if (step === 1) return <MerchantDetails formik={formik} />;
      if (step === 2) return <BasicDetails formik={formik} />;
      if (step === 3)
        return <ImageUploadStep formik={formik} onOpenCrop={setCropSrc} />;
      if (step === 4)
        return <FormSetup formik={formik} onFormSaved={() => setFormSaved(true)} />;
      if (step === 5) return <UdfMapping slug={formik.values.slug} />;
    }
    if (mode === "excel") {
      if (step === 1) return <MerchantDetails formik={formik} />;
      if (step === 2) return <BasicDetails formik={formik} />;
      if (step === 3)
        return <ImageUploadStep formik={formik} onOpenCrop={setCropSrc} />;
      if (step === 4) return <ExcelUploadStep formik={formik} />;
      if (step === 5)
        return <FormSetup onFormSaved={() => setFormSaved(true)} />;
      if (step === 6) return <UdfMapping slug={formik.values.slug} />;
    }
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={currentSchemas[step - 1] ?? null}
      validateOnMount={false}
      validateOnBlur={true}
      validateOnChange={true}
      onSubmit={submitFinal}
    >
      {(formik) => {
        formikRef.current = formik;

        const schema = currentSchemas[step - 1];
        const stepFields = schema?.fields ? Object.keys(schema.fields) : [];
        const isStepValid = stepFields.every((f) => !formik.errors[f]);

        return (
          <div className="px-shell">
            <Sidebar />
            <main className="px-main">
              <div className="px-page-head mb-4">
                <h2 className="px-title">Create Form</h2>
                <div className="px-sub">Choose mode and complete form</div>
              </div>

              <div className="px-card p-4">
                {/* ── IMPROVED MODE SELECTION ──────────────────────────────────── */}
                {step === 0 && (
                  <>
                    <CardHeader className="bg-transparent border-0 pb-4 text-center">
                      <h4 className="fw-bold mb-2">Choose Creation Mode</h4>
                      <p className="text-muted mb-0">
                        Select how you want to create your form
                      </p>
                    </CardHeader>

                    <CardBody className="pt-2 pb-5">
                      <div className="row g-4 justify-content-center">
                        {/* Manual Entry Card - Improved UI */}
                        <div className="col-md-5 col-lg-4">
                          <div
                            className={`card h-100 border shadow-sm text-center position-relative overflow-hidden ${
                              mode === "manual"
                                ? "border-primary border-3 shadow"
                                : "border border-2 border-light hover-border-primary"
                            }`}
                            style={{
                              borderRadius: "16px",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            onClick={() => handleModeSelect("manual")}
                            onMouseEnter={() => setMode("manual")}
                            onMouseLeave={() => setMode(null)}
                          >
                            <div className="card-body d-flex flex-column align-items-center justify-content-center p-5">
                              <div
                                className="rounded-circle p-4 mb-4 bg-light text-primary transition-all"
                                style={{
                                  width: "90px",
                                  height: "90px",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                <User size={42} />
                              </div>
                              <h5 className="fw-semibold mb-3 fs-4">
                                Manual Entry
                              </h5>
                              <p className="text-muted small mb-0 px-3">
                                Enter details step-by-step. Best for small
                                number of records or custom forms.
                              </p>
                            </div>

                            {/* Hover Effect Overlay */}
                          </div>
                        </div>

                        {/* Excel Upload Card - Improved UI */}
                        <div className="col-md-5 col-lg-4">
                          <div
                            className={`card h-100 border shadow-sm text-center position-relative overflow-hidden ${
                              mode === "excel"
                                ? "border-primary border-3 shadow"
                                : "border border-2 border-light hover-border-primary"
                            }`}
                            style={{
                              borderRadius: "16px",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            onClick={() => handleModeSelect("excel")}
                            onMouseEnter={() => setMode("excel")}
                            onMouseLeave={() => setMode(null)}
                          >
                            <div className="card-body d-flex flex-column align-items-center justify-content-center p-5">
                              <div
                                className="rounded-circle p-4 mb-4 bg-light text-success transition-all"
                                style={{
                                  width: "90px",
                                  height: "90px",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                <FileSpreadsheet size={42} />
                              </div>
                              <h5 className="fw-semibold mb-3 fs-4">
                                Excel Upload
                              </h5>
                              <p className="text-muted small mb-0 px-3">
                                Import bulk data from spreadsheet. Ideal for
                                large datasets.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </>
                )}

                {/* ── STEPS 1-N ────────────────────────────────────────────── */}
                {step >= 1 && (
                  <>
                    <ProgressStepper
                      currentStep={step - 1}
                      labels={currentStepLabels}
                      totalSteps={currentStepLabels.length}
                    />

                    <CardHeader className="bg-transparent border-0 pb-0">
                      <h6 className="text-muted mb-2">
                        {currentStepLabels[step - 1]}
                      </h6>
                    </CardHeader>

                    <CardBody>
                      <RSForm onSubmit={formik.handleSubmit}>
                        <div className="px-step-animate">
                          {renderStep(formik)}
                        </div>

                        <FormActions
                          currentStep={step}
                          totalSteps={totalSteps}
                          isSubmitting={formik.isSubmitting}
                          onBack={prevStep}
                          onNext={() => nextStep(formik)}
                          // isFormValid={isStepValid}
                          isFormValid={step === 3 ? formSaved : isStepValid}
                          formik={formik}
                        />
                      </RSForm>
                    </CardBody>
                  </>
                )}
              </div>

              <ImageCropModal
                isOpen={!!cropSrc}
                cropSrc={cropSrc}
                onClose={() => setCropSrc(null)}
                onSave={handleCropSave}
              />
            </main>
          </div>
        );
      }}
    </Formik>
  );
}
